import React, { Suspense, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import { LoadingSpinner } from "./Components/LoadingSpinner";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./lib/firebase";
import { getStoredIdToken, getTokenExpiryFromJwt, getEmailFromIdToken } from "./lib/tokenUtils";
import {
  firebaseSignOut,
  getFreshIdToken,
  exchangeSsoSession,
  waitForAuthReady,
} from "./lib/firebaseAuth";
import { clearCachedUserProfile, resolveAuthorisedUser } from "./utils/userUtils";
import { logout, setAuthorisedUser, setIsAuthorised } from "./Store/reducers/AppReducer";
import { persistor } from "./Store/store";
import { attachInterceptor } from "./utils/apiUtils/tokenMiddleware";
import useStatusToast from "./Hooks/useStatusToast";

let sharedEnforcedProfileFetch = null;
let ssoExchangePromise = null;

const SSO_SWITCH_RELOAD_KEY = "sso_identity_switch_reload";
const PER_USER_LOCAL_KEYS = ["lastUpn", "lastLoginHint", "emailForSignIn"];

const normalizeEmail = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const clearPreviousIdentity = async (dispatch) => {
  clearCachedUserProfile();
  dispatch(logout());
  await persistor.flush().catch(() => {});
  try {
    PER_USER_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
    sessionStorage.clear();
  } catch {
  }
};

// The dashboard handoff decides who is signed in. If it names a different
// person than the one I-9 last had, drop everything persisted for the previous
// person before any request goes out, then reload so no in-memory state survives.
const runSsoHandoff = async (dispatch, persistedProfileEmail) => {
  const restoredUser = await waitForAuthReady().catch(() => null);
  const previousEmails = [
    restoredUser?.email,
    getEmailFromIdToken(getStoredIdToken()),
    persistedProfileEmail,
  ]
    .map(normalizeEmail)
    .filter(Boolean);

  const user = await exchangeSsoSession();
  if (!user) return;

  const freshToken = await getFreshIdToken(false).catch(() => null);
  const newEmail = normalizeEmail(user.email) || normalizeEmail(getEmailFromIdToken(freshToken));
  const switched = Boolean(newEmail) && previousEmails.some((email) => email !== newEmail);

  const rememberIdentity = () => {
    if (!newEmail) return;
    try {
      localStorage.setItem("lastUpn", newEmail);
      localStorage.setItem("lastLoginHint", newEmail);
    } catch {
    }
  };

  if (!switched) {
    try {
      sessionStorage.removeItem(SSO_SWITCH_RELOAD_KEY);
    } catch {
    }
    rememberIdentity();
    return;
  }

  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(SSO_SWITCH_RELOAD_KEY) === newEmail;
  } catch {
  }
  await clearPreviousIdentity(dispatch);
  rememberIdentity();
  await getFreshIdToken(false).catch(() => null);
  attachInterceptor(null, dispatch);
  if (alreadyReloaded) return;
  try {
    sessionStorage.setItem(SSO_SWITCH_RELOAD_KEY, newEmail);
  } catch {
  }
  window.location.reload();
  await new Promise(() => {});
};

const ProtectedRoute = ({ Module, allowedRoles, enforceAccess = false }) => {
  const { isAuthorised, authorisedUser } = useSelector((state) => state.App);
  const dispatch = useDispatch();
  const { showError, ToastPortal } = useStatusToast();

  const [authUser, setAuthUser] = useState(undefined);
  const [profileChecked, setProfileChecked] = useState(!enforceAccess);
  const [redirectAfterDenial, setRedirectAfterDenial] = useState(false);
  const signoutInProgress = useRef(false);
  const deniedToastShownRef = useRef(false);
  const profileFetchStartedRef = useRef(false);
  const profileFetchInFlightRef = useRef(false);
  const denialTimerRef = useRef(null);
  const [ssoChecked, setSsoChecked] = useState(false);

  const isEmployeeOnlyRoute =
    Array.isArray(allowedRoles) && allowedRoles.length === 1 && allowedRoles[0] === 3;

  useEffect(() => {
    if (isEmployeeOnlyRoute) {
      setSsoChecked(true);
      return;
    }
    if (!ssoExchangePromise) {
      ssoExchangePromise = runSsoHandoff(dispatch, authorisedUser?.email).catch(() => null);
    }
    let cancelled = false;
    ssoExchangePromise.finally(() => {
      if (!cancelled) setSsoChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isEmployeeOnlyRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthUser(user);
        getFreshIdToken(false).catch(() => {});
        return;
      }

      const token = getStoredIdToken();
      const expiry = getTokenExpiryFromJwt(token);
      const validToken = Boolean(token && (!expiry || expiry > Date.now()));
      setAuthUser(validToken ? { tokenUser: true } : null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkSharedLogout = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        getFreshIdToken(false).catch(() => {});
        return;
      }

      const token = getStoredIdToken();
      const expiry = getTokenExpiryFromJwt(token);
      const validToken = Boolean(token && (!expiry || expiry > Date.now()));
      if (validToken) return;
      const refreshed = await getFreshIdToken(true).catch(() => null);
      if (refreshed) {
        setAuthUser({ tokenUser: true });
        return;
      }
      if (!signoutInProgress.current) {
        signoutInProgress.current = true;
        firebaseSignOut()
          .catch(() => {})
          .finally(() => {
            signoutInProgress.current = false;
          });
      }
      setAuthUser(null);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSharedLogout();
      }
    };

    window.addEventListener("focus", checkSharedLogout);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const intervalId = window.setInterval(checkSharedLogout, 30000);

    return () => {
      window.removeEventListener("focus", checkSharedLogout);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (enforceAccess || !authUser?.tokenUser || authorisedUser || !ssoChecked) return;
    const emailHint = getEmailFromIdToken(getStoredIdToken()) || "";
    resolveAuthorisedUser({ email: emailHint })
      .then(({ profile }) => {
        if (profile) {
          dispatch(setAuthorisedUser(profile));
          dispatch(setIsAuthorised(true));
        }
      })
      .catch(() => {});
  }, [enforceAccess, authUser?.tokenUser, authorisedUser, ssoChecked, dispatch]);

  const trulyUnmountedRef = useRef(false);
  useEffect(() => {
    return () => {
      trulyUnmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!enforceAccess) return;
    if (authUser === undefined) return; 
    if (!ssoChecked) return;
    if (!authUser) {
      profileFetchStartedRef.current = false; 
      setProfileChecked(true); 
      return;
    }
    if (profileFetchStartedRef.current) return;
    profileFetchStartedRef.current = true;
    profileFetchInFlightRef.current = true;
    setProfileChecked(false);

    if (!sharedEnforcedProfileFetch) {
      const emailHint = authUser?.email || getEmailFromIdToken(getStoredIdToken()) || "";
      sharedEnforcedProfileFetch = resolveAuthorisedUser({
        email: emailHint,
        forceRefresh: true,
      }).finally(() => {
        setTimeout(() => {
          sharedEnforcedProfileFetch = null;
        }, 3000);
      });
    }

    sharedEnforcedProfileFetch
      .then(({ profile }) => {
        if (trulyUnmountedRef.current) return;
        if (profile) {
          dispatch(setAuthorisedUser(profile));
          dispatch(setIsAuthorised(true));
        }
      })
      .catch(() => {})
      .finally(() => {
        profileFetchInFlightRef.current = false;
        if (!trulyUnmountedRef.current) setProfileChecked(true);
      });
  }, [enforceAccess, authUser, ssoChecked, dispatch]);

  const hasAccess =
    (allowedRoles.includes(0) && authorisedUser?.isSuperAdmin) ||
    authorisedUser?.roles?.some((role) => allowedRoles.includes(role?.role));
  useEffect(() => {
    if (!hasAccess || !denialTimerRef.current) return;
    clearTimeout(denialTimerRef.current);
    denialTimerRef.current = null;
    deniedToastShownRef.current = false;
  }, [hasAccess]);

  useEffect(() => () => clearTimeout(denialTimerRef.current), []);

  useEffect(() => {
    if (
      !enforceAccess ||
      !profileChecked ||
      !authUser ||
      profileFetchInFlightRef.current ||
      deniedToastShownRef.current
    )
      return;
    if (!hasAccess) {
      deniedToastShownRef.current = true;
      showError("You don't have access to this page.");
      denialTimerRef.current = setTimeout(() => {
        denialTimerRef.current = null;
        firebaseSignOut()
          .catch(() => {})
          .finally(() => {
            try {
              localStorage.clear();
              sessionStorage.clear();
            } catch {
            }
            dispatch(setAuthorisedUser(null));
            dispatch(setIsAuthorised(false));
            setRedirectAfterDenial(true);
          });
      }, 2000);
    }
  }, [enforceAccess, profileChecked, authUser, hasAccess, showError, dispatch]);

  if (
    authUser === undefined ||
    !ssoChecked ||
    (!isAuthorised && authUser && !authUser?.tokenUser)
  ) {
    return (
      <>
        {ToastPortal}
        <div>
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (!authUser) {
    const intended = window.location.pathname + window.location.search;
    if (intended && intended !== "/") {
      sessionStorage.setItem("pending_redirect_url", intended);
    }
    return <Navigate to="/" />;
  }

  if (enforceAccess) {
    if (!profileChecked) {
      return (
        <div>
          <LoadingSpinner />
        </div>
      );
    }
    if (!hasAccess) {
      if (redirectAfterDenial) {
        return <Navigate to="/" replace />;
      }
      return (
        <>
          {ToastPortal}
          <div>
            <LoadingSpinner />
          </div>
        </>
      );
    }
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Module />
    </Suspense>
  );
};

export default ProtectedRoute;
